import {
  InAppMessageImpression,
  InAppMessageImpressionStorage
} from "../../../../hackle/iam/storage/InAppMessageImpressionStorage"
import {
  HackleUser,
  InAppMessage,
  InAppMessageDurationCap,
  InAppMessageEventFrequencyCap,
  InAppMessageIdentifierCap,
  InAppMessageUserOverride
} from "../../model/model"
import ObjectUtil from "../../util/ObjectUtil"
import { EvaluatorContext } from "../evalautor/Evaluator"
import { InAppMessageEligibilityRequest } from "../evalautor/iam/eligibility/InAppMessageEligibilityRequest"
import TargetMatcher from "../match/TargetMatcher"
import InAppMessageHiddenStorage from "./InAppMessageHiddenStorage"

export interface InAppMessageMatcher {
  matches(request: InAppMessageEligibilityRequest, context: EvaluatorContext): boolean
}

export class UserOverrideInAppMessageMatcher implements InAppMessageMatcher {
  matches(request: InAppMessageEligibilityRequest, context: EvaluatorContext): boolean {
    return request.inAppMessage.targetContext.overrides.some((it) => this.isUserOverridden(request, it))
  }

  private isUserOverridden(request: InAppMessageEligibilityRequest, userOverride: InAppMessageUserOverride): boolean {
    const identifier = request.user.identifiers[userOverride.identifierType]
    if (ObjectUtil.isNullOrUndefined(identifier)) {
      return false
    }
    return userOverride.identifiers.includes(identifier)
  }
}

export class TargetInAppMessageMatcher implements InAppMessageMatcher {
  constructor(private readonly targetMatcher: TargetMatcher) {}

  matches(request: InAppMessageEligibilityRequest, context: EvaluatorContext): boolean {
    const targets = request.inAppMessage.targetContext.targets
    if (targets.length === 0) {
      return true
    }
    return targets.some((it) => this.targetMatcher.matches(request, context, it))
  }
}

export class HiddenInAppMessageMatcher implements InAppMessageMatcher {
  constructor(private readonly storage: InAppMessageHiddenStorage) {}

  matches(request: InAppMessageEligibilityRequest, context: EvaluatorContext): boolean {
    return this.storage.exist(request.inAppMessage, request.timestamp)
  }
}

export class InAppMessageFrequencyCapMatcher implements InAppMessageMatcher {
  constructor(private storage: InAppMessageImpressionStorage) {}

  matches(request: InAppMessageEligibilityRequest, context: EvaluatorContext): boolean {
    const { inAppMessage, user, timestamp } = request

    return this.isFrequencyCapped(inAppMessage, user, timestamp)
  }

  private isFrequencyCapped(inAppMessage: InAppMessage, user: HackleUser, timestamp: number): boolean {
    if (!inAppMessage.eventTrigger.frequencyCap) {
      return false
    }

    const contexts = this.createMatchContexts(inAppMessage.eventTrigger.frequencyCap)
    if (contexts.length === 0) {
      return false
    }

    const impressions = this.storage.get(inAppMessage)
    for (const impression of impressions) {
      for (const context of contexts) {
        if (context.matches(user, timestamp, impression)) {
          return true
        }
      }
    }

    return false
  }

  private createMatchContexts(frequencyCap: InAppMessageEventFrequencyCap): MatchContext[] {
    const contexts: MatchContext[] = []

    for (const identifierCap of frequencyCap.identifierCaps) {
      contexts.push(new MatchContext(new IdentifierCapPredicate(identifierCap)))
    }

    if (frequencyCap.durationCap) {
      contexts.push(new MatchContext(new DurationCapPredicate(frequencyCap.durationCap)))
    }

    return contexts
  }
}

class MatchContext {
  private readonly predicate: FrequencyCapPredicate
  private matchCount = 0

  constructor(predicate: FrequencyCapPredicate) {
    this.predicate = predicate
  }

  public matches(user: HackleUser, timestamp: number, impression: InAppMessageImpression): boolean {
    if (this.predicate.matches(user, timestamp, impression)) {
      this.matchCount += 1
    }
    return this.matchCount >= this.predicate.thresholdCount
  }
}

interface FrequencyCapPredicate {
  thresholdCount: number

  matches(user: HackleUser, timestamp: number, impression: InAppMessageImpression): boolean
}

export class IdentifierCapPredicate implements FrequencyCapPredicate {
  constructor(private readonly identifierCap: InAppMessageIdentifierCap) {}

  get thresholdCount(): number {
    return this.identifierCap.count
  }

  matches(user: HackleUser, _timestamp: number, impression: InAppMessageImpression): boolean {
    const userIdentifier = user.identifiers[this.identifierCap.identifierType]
    if (ObjectUtil.isNullOrUndefined(userIdentifier)) return false

    const impressionIdentifier = impression.identifiers[this.identifierCap.identifierType]
    if (ObjectUtil.isNullOrUndefined(impressionIdentifier)) return false

    return userIdentifier === impressionIdentifier
  }
}

export class DurationCapPredicate implements FrequencyCapPredicate {
  constructor(private readonly durationCap: InAppMessageDurationCap) {}

  get thresholdCount(): number {
    return this.durationCap.count
  }

  matches(_user: HackleUser, timestamp: number, impression: InAppMessageImpression): boolean {
    return timestamp - impression.timestamp <= this.durationCap.durationMillis
  }
}
